import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Shield,
  Phone,
  Mail,
  Edit2,
  Trash2,
  Lock,
  FileText
} from 'lucide-react';
import { fetchData } from '../api/api';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import Button from '../components/common/Button';
import InputField from '../components/common/InputField';
import SelectField from '../components/common/SelectField';
import Modal from '../components/common/Modal';
import Table from '../components/common/Table';

const UsersPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    bio: '',
    roleId: '',
    password: ''
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchData('api/user/all', 'GET', null, authToken, selectedShopCode);
      if (response.status) {
        setUsers(response.data || []);
      }
    } catch (error) {
      showError('Failed to load user accounts.');
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, showError]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await fetchData('api/role/all', 'GET', null, authToken, selectedShopCode);
      if (response.status) {
        setRoles(response.data || []);
      }
    } catch (error) {
      // ignore
    }
  }, [authToken, selectedShopCode]);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [loadUsers, loadRoles]);

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      bio: '',
      roleId: '',
      password: ''
    });
    setEditingUser(null);
    setIsCreating(false);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreating(true);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      email: user.email || '',
      bio: user.bio || '',
      roleId: user.roleId || '',
      password: ''
    });
    setIsCreating(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showError('Name is required');
      return;
    }
    if (!formData.phone.trim()) {
      showError('Phone number is required');
      return;
    }
    if (!formData.roleId) {
      showError('Role assignment is required');
      return;
    }
    if (isCreating && !formData.password.trim()) {
      showError('Password is required for new users');
      return;
    }

    try {
      if (isCreating) {
        const createData = {
          name: formData.name,
          phone: formData.phone,
          email: formData.email || '',
          bio: formData.bio || '',
          roleId: formData.roleId,
          password: formData.password
        };

        const response = await fetchData('api/user/create', 'POST', createData, authToken, selectedShopCode);
        if (response.status) {
          showSuccess('Staff user created successfully.');
          setShowModal(false);
          resetForm();
          loadUsers();
        }
      } else {
        const updateData = {
          name: formData.name,
          phone: formData.phone,
          email: formData.email || '',
          bio: formData.bio || '',
          roleId: formData.roleId
        };

        if (formData.password && formData.password.trim() !== '') {
          updateData.password = formData.password;
        }

        const response = await fetchData(`api/user/update/${editingUser.id}`, 'POST', updateData, authToken, selectedShopCode);
        if (response.status) {
          showSuccess('User account updated successfully.');
          setShowModal(false);
          resetForm();
          loadUsers();
        }
      }
    } catch (error) {
      showError(error.message || 'Operation failed');
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete this staff account?')) {
      try {
        const response = await fetchData(`api/user/remove/${userId}`, 'DELETE', null, authToken, selectedShopCode);
        if (response.status) {
          showSuccess('User removed successfully.');
          loadUsers();
        }
      } catch (error) {
        showError('Failed to remove user account.');
      }
    }
  };

  const roleOptions = [
    { label: 'Select Assigned Role', value: '' },
    ...roles.map(r => ({ label: r.name, value: r.id }))
  ];

  const filteredUsers = users.filter(user => {
    const matchesSearch = (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (user.phone || '').includes(searchTerm) ||
                          (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || user.roleId === roleFilter;
    return matchesSearch && matchesRole;
  });

  const tableHeaders = ['Staff Member', 'Contact Phone', 'Email Address', 'Role Permission', 'Registered', 'Actions'];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff & User Management"
        subtitle="Manage employee logins, cashier assignments, and role permissions"
        breadcrumbs={[{ label: 'Users' }]}
        action={
          <Button
            onClick={openCreateModal}
            icon={<UserPlus className="w-4 h-4" />}
            size="sm"
          >
            Add New Staff
          </Button>
        }
      />

      {/* Filter Bar */}
      <GlassmorphicCard noPadding>
        <div className="p-4 md:p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto flex-1">
            <div className="w-full sm:w-80">
              <InputField
                placeholder="Search staff name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-48">
              <SelectField
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                options={[
                  { label: 'All Staff Roles', value: '' },
                  ...roles.map(r => ({ label: r.name, value: r.id }))
                ]}
                className="!mb-0"
              />
            </div>
          </div>

          <div className="text-xs text-slate-400">
            {filteredUsers.length} Staff Accounts
          </div>
        </div>
      </GlassmorphicCard>

      {/* Users Table */}
      <Table
        headers={tableHeaders}
        data={filteredUsers}
        loading={loading}
        emptyMessage={searchTerm || roleFilter ? 'No staff users match your search.' : 'No staff accounts found.'}
        renderRow={(user) => {
          const initials = (user.name || 'U')
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase();

          return (
            <tr
              key={user.id}
              className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-600/10 text-emerald-600 dark:text-emerald-400 font-bold text-xs flex items-center justify-center shrink-0 border border-emerald-500/20">
                    {initials}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white text-sm block">
                      {user.name}
                    </span>
                    {user.bio && (
                      <span className="text-[11px] text-slate-400 truncate max-w-[160px] block">
                        {user.bio}
                      </span>
                    )}
                  </div>
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300 font-mono">
                {user.phone}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                {user.email || '—'}
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                  <Shield className="w-3 h-3" />
                  {user.roleName || 'Staff'}
                </span>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-400">
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex items-center justify-end gap-1.5">
                  <Button
                    onClick={() => openEditModal(user)}
                    variant="light"
                    size="sm"
                    icon={<Edit2 className="w-3 h-3" />}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={() => handleDelete(user.id)}
                    variant="light"
                    size="sm"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    icon={<Trash2 className="w-3 h-3" />}
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          );
        }}
      />

      {/* Create / Edit User Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={isCreating ? 'Create New Staff Account' : `Edit Account • ${editingUser?.name || ''}`}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputField
            label="Full Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Samuel Osei"
            required
            icon={<Users className="w-4 h-4" />}
          />

          <InputField
            label="Phone Number"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="e.g. +233 24 000 0000"
            required
            icon={<Phone className="w-4 h-4" />}
          />

          <InputField
            label="Email Address (Optional)"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="e.g. staff@swiftsale.com"
            icon={<Mail className="w-4 h-4" />}
          />

          <SelectField
            label="Assigned Role"
            value={formData.roleId}
            onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
            options={roleOptions}
            required
            icon={<Shield className="w-4 h-4" />}
          />

          <InputField
            label="Role Bio / Staff Title"
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            placeholder="e.g. Morning Shift Cashier"
            icon={<FileText className="w-4 h-4" />}
          />

          <InputField
            label={isCreating ? "Account Password" : "New Password (leave blank to keep existing)"}
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="••••••••••••"
            required={isCreating}
            icon={<Lock className="w-4 h-4" />}
          />

          <div className="pt-2">
            <Button type="submit" className="w-full py-3">
              {isCreating ? 'Create Staff User' : 'Update User Account'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UsersPage;